// src/pages/products/EditProductPage/components/VariationsTab.jsx
import React from 'react';
import {
  AlertCircle,
  ChevronUp,
  RefreshCw,
  Zap,
  Copy,
  CheckSquare,
  Square,
  Trash2,
  Edit3,
  Settings,
  Tag,
  Hash,
  DollarSign,
  Weight,
  Building2,
  X,
  Loader2,
  Truck
} from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import styles from '../styles/VariationsTab.module.css';

const VariationsTab = ({
  formData,
  attributes,
  shops,
  errors,
  skuValidation,
  variationCombinations,
  expandedVariations,
  selectedVariations,
  showBulkActions,
  bulkShopAssignments,
  bulkPrices,
  bulkCostPrice,
  bulkWeight,
  setActiveTab,
  toggleVariationExpansion,
  toggleVariationSelection,
  selectAllVariations,
  deselectAllVariations,
  removeSelectedVariations,
  regenerateVariations,
  handleBulkShopAssignment,
  handleBulkPriceChange,
  setBulkCostPrice,
  setBulkWeight,
  setShowBulkActions,
  applyBulkActions,
  handleVariationChange,
  handleVariationShopDataChange,
  handleVariationImageChange,
  isEdit = false
}) => {
  const { t } = useLocalization();

  if (formData.productType !== 'variable') return null;

  const variationAttributes = formData.attributeIds.filter(attr => attr.usedForVariations);
  const hasSelectedValues = formData.attributeValueIds.length > 0;

  if (variationAttributes.length === 0 || !hasSelectedValues) {
    return (
      <div className={styles.formCard}>
        <div className={styles.cardHeader}>
          <h2>{t('products.productVariations')}</h2>
        </div>
        <div className={styles.cardContent}>
          <div className={styles.noVariationsMessage}>
            <AlertCircle size={48} className={styles.noVariationsIcon} />
            <h3>{t('products.noVariationsAvailable')}</h3>
            <p>{t('products.noVariationsMessage')}</p>
            <ol>
              <li>{t('products.goToAttributesStep1')} <strong>{t('products.attributes')}</strong> {t('products.goToAttributesStep2')}</li>
              <li>{t('products.goToAttributesStep3')}</li>
              <li>{t('products.goToAttributesStep4')}</li>
              <li>{t('products.goToAttributesStep5')}</li>
            </ol>
            <button type="button" onClick={() => setActiveTab('attributes')} className={styles.goToAttributesButton}>
              <Tag size={16} />
              {t('products.goToAttributes')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.formCard}>
      <div className={styles.cardHeader}>
        <h2>{t('products.productVariations')}</h2>
        {isEdit && (
          <p className={styles.editModeNote}>{t('products.editModeVariationsNote')}</p>
        )}
      </div>
      <div className={styles.cardContent}>
        {formData.variations.length > 0 ? (
          <div className={styles.variationsContainer}>
            <div className={styles.variationsHeader}>
              <div className={styles.variationsHeaderLeft}>
                <h3>{t('products.variationsGenerated', { count: formData.variations.length })}</h3>
                <p>{isEdit ? t('products.configureVariationsEdit') : t('products.configureVariations')}</p>
              </div>
              <div className={styles.variationsHeaderRight}>
                <button type="button" onClick={regenerateVariations} className={styles.regenerateButton}>
                  <RefreshCw size={16} />
                  {t('products.regenerate')}
                </button>
              </div>
            </div>

            {/* Variation Management Actions */}
            <div className={styles.variationActions}>
              <div className={styles.variationActionsLeft}>
                <button
                  type="button"
                  onClick={selectedVariations.size === formData.variations.length ? deselectAllVariations : selectAllVariations}
                  className={styles.selectAllButton}
                >
                  {selectedVariations.size === formData.variations.length ? (
                    <><Square size={16} />{t('products.deselectAll')}</>
                  ) : (
                    <><CheckSquare size={16} />{t('products.selectAll')}</>
                  )}
                </button>

                {selectedVariations.size > 0 && (
                  <>
                    <button type="button" onClick={removeSelectedVariations} className={styles.removeSelectedButton}>
                      <Trash2 size={16} />
                      {t('products.removeSelected', { count: selectedVariations.size })}
                    </button>
                    <button type="button" onClick={() => setShowBulkActions(!showBulkActions)} className={styles.bulkActionsButton}>
                      <Zap size={16} />
                      {t('products.bulkActions')}
                    </button>
                  </>
                )}
              </div>
              <div className={styles.variationActionsRight}>
                <span className={styles.selectionCount}>
                  {t('products.variationsSelected', { selected: selectedVariations.size, total: formData.variations.length })}
                </span>
              </div>
            </div>

            {/* Bulk Actions Panel */}
            {showBulkActions && selectedVariations.size > 0 && (
              <div className={styles.bulkActionsPanel}>
                <div className={styles.bulkActionsHeader}>
                  <h4>{t('products.bulkActionsTitle')}</h4>
                  <p>{t('products.bulkActionsDesc', { count: selectedVariations.size })}</p>
                </div>
                <div className={styles.bulkActionsContent}>
                  <div className={styles.bulkShopAssignments}>
                    <h5>{t('products.shopAssignments')}</h5>
                    <div className={styles.bulkShopGrid}>
                      {shops.map((shop) => (
                        <div key={shop.id} className={styles.bulkShopCard}>
                          <label className={styles.bulkShopCheckbox}>
                            <input
                              type="checkbox"
                              checked={bulkShopAssignments[shop.id] || false}
                              onChange={(e) => handleBulkShopAssignment(shop.id, e.target.checked)}
                            />
                            <Building2 size={14} className={styles.shopIcon} />
                            <span className={styles.shopName}>{shop.name}</span>
                          </label>

                          {bulkShopAssignments[shop.id] && (
                            <div className={styles.bulkPriceInputs}>
                              <div className={styles.bulkPriceInput}>
                                <label>{t('products.salesPrice')}</label>
                                <div className={styles.inputWithIcon}>
                                  <DollarSign size={14} className={styles.inputIcon} />
                                  <input
                                    type="number"
                                    value={bulkPrices[shop.id] || ''}
                                    onChange={(e) => handleBulkPriceChange(shop.id, e.target.value)}
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                  />
                                </div>
                              </div>
                              {shop.type === 'dropshipper' && (
                                <div className={styles.bulkPriceInput}>
                                  <label>{t('products.dropshippingPrice')}</label>
                                  <div className={styles.inputWithIcon}>
                                    <DollarSign size={14} className={styles.inputIcon} />
                                    <input
                                      type="number"
                                      value={bulkPrices[`${shop.id}_dropshipping`] || ''}
                                      onChange={(e) => handleBulkPriceChange(`${shop.id}_dropshipping`, e.target.value)}
                                      placeholder="0.00"
                                      step="0.01"
                                      min="0"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.bulkBasicFields}>
                    <h5>{t('products.basicFields')}</h5>
                    <div className={styles.bulkFieldsGrid}>
                      <div className={styles.bulkFieldInput}>
                        <label>{t('products.costPrice')}</label>
                        <div className={styles.inputWithIcon}>
                          <DollarSign size={14} className={styles.inputIcon} />
                          <input type="number" value={bulkCostPrice} onChange={(e) => setBulkCostPrice(e.target.value)} placeholder="0.00" step="0.01" min="0" />
                        </div>
                      </div>
                      <div className={styles.bulkFieldInput}>
                        <label>{t('products.weight')} (kg)</label>
                        <div className={styles.inputWithIcon}>
                          <Weight size={14} className={styles.inputIcon} />
                          <input type="number" value={bulkWeight} onChange={(e) => setBulkWeight(e.target.value)} placeholder="0.00" step="0.01" min="0" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.bulkActionsFooter}>
                    <button type="button" onClick={() => setShowBulkActions(false)} className={styles.cancelBulkButton}>
                      {t('products.cancel')}
                    </button>
                    <button type="button" onClick={applyBulkActions} className={styles.applyBulkButton}>
                      <Copy size={16} />
                      {t('products.applyToSelected')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Variations List */}
            {formData.variations.map((variation, variationIndex) => (
              <div key={variation.id || `new-${variationIndex}`} className={styles.variationCard}>
                <div className={styles.variationHeader}>
                  <div className={styles.variationHeaderLeft}>
                    <label className={styles.variationCheckbox}>
                      <input
                        type="checkbox"
                        checked={selectedVariations.has(variation.id)}
                        onChange={() => toggleVariationSelection(variation.id)}
                      />
                    </label>
                    <div className={styles.variationTitle}>
                      <h4>{t('products.variationNumber', { number: variationIndex + 1 })}</h4>
                      <div className={styles.variationAttributes}>
                        {variation.attributeValues?.map((value, idx) => (
                          <span key={idx} className={styles.attributeBadge}>{value.value}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className={styles.variationHeaderRight}>
                    <button type="button" onClick={() => toggleVariationExpansion(variation.id)} className={styles.expandButton}>
                      {expandedVariations.has(variation.id) ? (
                        <><ChevronUp size={16} />{t('products.collapse')}</>
                      ) : (
                        <><Edit3 size={16} />{t('products.edit')}</>
                      )}
                    </button>
                  </div>
                </div>

                {expandedVariations.has(variation.id) && (
                  <div className={styles.variationContent}>
                    {/* Basic Variation Info */}
                    <div className={styles.variationBasicInfo}>
                      <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label>{t('products.sku')}</label>
                          <div className={styles.inputWithIcon}>
                            <Hash size={16} className={styles.inputIcon} />
                            <input
                              type="text"
                              value={variation.sku}
                              onChange={(e) => handleVariationChange(variationIndex, 'sku', e.target.value)}
                              placeholder={t('products.skuVariationPlaceholder')}
                              className={errors.variations?.[variationIndex]?.sku ? 'error' : ''}
                            />
                            {skuValidation?.variations?.[variationIndex]?.isValidating && (
                              <Loader2 size={16} className={`${styles.inputIcon} ${styles.validationSpinner}`} />
                            )}
                          </div>
                          {errors.variations?.[variationIndex]?.sku && (
                            <span className={styles.errorText}>{errors.variations[variationIndex].sku}</span>
                          )}
                        </div>

                        <div className={styles.formGroup}>
                          <label>{t('products.barcode')}</label>
                          <div className={styles.inputWithIcon}>
                            <Hash size={16} className={styles.inputIcon} />
                            <input
                              type="text"
                              value={variation.barcode}
                              onChange={(e) => handleVariationChange(variationIndex, 'barcode', e.target.value)}
                              placeholder={t('products.barcodePlaceholder')}
                            />
                          </div>
                        </div>
                      </div>

                      <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label>{t('products.costPrice')}</label>
                          <div className={styles.inputWithIcon}>
                            <DollarSign size={16} className={styles.inputIcon} />
                            <input
                              type="number"
                              value={variation.costPrice}
                              onChange={(e) => handleVariationChange(variationIndex, 'costPrice', e.target.value)}
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>

                        <div className={styles.formGroup}>
                          <label>{t('products.weight')} (kg)</label>
                          <div className={styles.inputWithIcon}>
                            <Weight size={16} className={styles.inputIcon} />
                            <input
                              type="number"
                              value={variation.weight}
                              onChange={(e) => handleVariationChange(variationIndex, 'weight', e.target.value)}
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Variation Image */}
                      <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label>{t('products.variationImage')}</label>
                          <div className={styles.variationImageUpload}>
                            <input
                              type="file"
                              accept=".png,.jpg,.jpeg,.webp"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) handleVariationImageChange(variationIndex, file);
                              }}
                              className={styles.variationImageInput}
                            />

                            {!variation.variationImage && variation.existingImages?.length > 0 && (
                              <div className={styles.existingVariationImagePreview}>
                                <img src={variation.existingImages[0].url} alt="Existing variation image" className={styles.variationPreviewImage} />
                                <div className={styles.existingImageLabel}>{t('products.currentImage')}</div>
                                {isEdit && (
                                  <button type="button" onClick={() => handleVariationChange(variationIndex, 'deleteImage', true)} className={styles.removeVariationImage}>
                                    <X size={12} />
                                  </button>
                                )}
                              </div>
                            )}

                            {variation.variationImage && (
                              <div className={styles.variationImagePreview}>
                                <img src={URL.createObjectURL(variation.variationImage)} alt="New variation preview" className={styles.variationPreviewImage} />
                                <div className={styles.newImageLabel}>{t('products.newImage')}</div>
                                <button type="button" onClick={() => handleVariationImageChange(variationIndex, null)} className={styles.removeVariationImage}>
                                  <X size={12} />
                                </button>
                              </div>
                            )}

                            {!variation.variationImage && (!variation.existingImages || variation.existingImages.length === 0) && (
                              <div className={styles.imageUploadInstructions}>{t('products.chooseImageFile')}</div>
                            )}

                            {variation.deleteImage && (
                              <div className={styles.deleteImageIndicator}>
                                <AlertCircle size={16} />
                                <span>{t('products.currentImageWillBeDeleted')}</span>
                                <button type="button" onClick={() => handleVariationChange(variationIndex, 'deleteImage', false)} className={styles.undoDeleteButton}>
                                  {t('products.undoDelete')}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Shop Pricing */}
                    <div className={styles.variationShopData}>
                      <h5>{t('products.shopPricing')}</h5>
                      {isEdit && (
                        <p className={styles.editStockNotice}>{t('products.editModeStockNotice')}</p>
                      )}
                      <div className={styles.shopDataGrid}>
                        {shops.map((shop, shopIndex) => (
                          <div
                            key={shop.id}
                            className={`${styles.variationShopCard} ${variation.shopData[shopIndex]?.enabled ? styles.shopCardEnabled : ''}`}
                          >
                            <div className={styles.shopHeader}>
                              <label className={styles.shopCheckbox}>
                                <input
                                  type="checkbox"
                                  checked={variation.shopData[shopIndex]?.enabled || false}
                                  onChange={(e) => handleVariationShopDataChange(variationIndex, shopIndex, 'enabled', e.target.checked)}
                                />
                                <Building2 size={14} className={styles.shopIcon} />
                                <span className={styles.shopName}>{shop.name}</span>
                              </label>
                              <span className={styles.shopType}>{shop.type}</span>
                            </div>

                            {variation.shopData[shopIndex]?.enabled && (
                              <div className={styles.variationShopFields}>
                                <div className={styles.formGroup}>
                                  <label>{t('products.salesPrice')} *</label>
                                  <div className={styles.inputWithIcon}>
                                    <DollarSign size={14} className={styles.inputIcon} />
                                    <input
                                      type="number"
                                      value={variation.shopData[shopIndex]?.salesPrice ?? ''}
                                      onChange={(e) => handleVariationShopDataChange(variationIndex, shopIndex, 'salesPrice', e.target.value)}
                                      placeholder="0.00"
                                      step="0.01"
                                      min="0"
                                      className={errors.variations?.[variationIndex]?.shopDataDetails?.[shopIndex]?.salesPrice ? 'error' : ''}
                                      required
                                    />
                                  </div>
                                  {errors.variations?.[variationIndex]?.shopDataDetails?.[shopIndex]?.salesPrice && (
                                    <span className={styles.errorText}>
                                      {errors.variations[variationIndex].shopDataDetails[shopIndex].salesPrice}
                                    </span>
                                  )}
                                </div>

                                {shop.type === 'dropshipper' && (
                                  <div className={styles.formGroup}>
                                    <label>{t('products.dropshippingPrice')} *</label>
                                    <div className={styles.inputWithIcon}>
                                      <DollarSign size={14} className={styles.inputIcon} />
                                      <input
                                        type="number"
                                        value={variation.shopData[shopIndex]?.dropshippingPrice || ''}
                                        onChange={(e) => handleVariationShopDataChange(variationIndex, shopIndex, 'dropshippingPrice', e.target.value)}
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                        className={errors.variations?.[variationIndex]?.shopDataDetails?.[shopIndex]?.dropshippingPrice ? 'error' : ''}
                                        required
                                      />
                                    </div>
                                    {errors.variations?.[variationIndex]?.shopDataDetails?.[shopIndex]?.dropshippingPrice && (
                                      <span className={styles.errorText}>
                                        {errors.variations[variationIndex].shopDataDetails[shopIndex].dropshippingPrice}
                                      </span>
                                    )}
                                  </div>
                                )}

                                {/* Override Delivery Charge */}
                                <div className={styles.overrideDeliverySection}>
                                  <label className={styles.overrideDeliveryCheckbox}>
                                    <input
                                      type="checkbox"
                                      checked={variation.shopData[shopIndex]?.overrideDeliveryChargeEnabled || false}
                                      onChange={(e) => {
                                        handleVariationShopDataChange(variationIndex, shopIndex, 'overrideDeliveryChargeEnabled', e.target.checked);
                                        if (!e.target.checked) {
                                          handleVariationShopDataChange(variationIndex, shopIndex, 'overrideDeliveryCharge', null);
                                        }
                                      }}
                                    />
                                    <Truck size={14} className={styles.overrideDeliveryIcon} />
                                    <span>Override Delivery Charge</span>
                                  </label>

                                  {variation.shopData[shopIndex]?.overrideDeliveryChargeEnabled && (
                                    <div className={styles.formGroup}>
                                      <div className={styles.inputWithIcon}>
                                        <DollarSign size={14} className={styles.inputIcon} />
                                        <input
                                          type="number"
                                          value={variation.shopData[shopIndex]?.overrideDeliveryCharge ?? ''}
                                          onChange={(e) =>
                                            handleVariationShopDataChange(variationIndex, shopIndex, 'overrideDeliveryCharge', e.target.value)
                                          }
                                          placeholder="0.00"
                                          step="0.01"
                                          min="0"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {errors.variations?.[variationIndex]?.shopData && (
                        <span className={styles.errorText}>{errors.variations[variationIndex].shopData}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.noVariationsMessage}>
            <Settings size={48} className={styles.noVariationsIcon} />
            <h3>{t('products.noVariationsGenerated')}</h3>
            <p>{t('products.noVariationsGeneratedDesc')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VariationsTab;